import React, { useState, useEffect, useMemo } from 'react';
import { BarChart3, ArrowUpDown, ArrowUp, ArrowDown, Target, Clock, DollarSign, ChevronDown, X, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react';
import { useDashboard } from '../context/DashboardContext';
import ProjectDetailModal from './ProjectDetailModal';

// Import MultiSelect component from MetricsOverview
const MultiSelect = ({ label, options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (value) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const handleSelectAll = () => {
    onChange(options);
  };

  const handleDeselectAll = () => {
    onChange([]);
  };

  const handleClearAll = () => {
    onChange([]);
  };

  const displayText = selectedValues.length === 0 
    ? placeholder 
    : selectedValues.length === 1 
      ? selectedValues[0] 
      : `${selectedValues.length} selected`;

  return (
    <div className="multi-select-container" ref={dropdownRef}>
      <button
        type="button"
        className="multi-select-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="multi-select-text">{displayText}</span>
        <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="multi-select-dropdown">
          <div className="multi-select-header">
            <button
              type="button"
              className="select-all-button"
              onClick={selectedValues.length === options.length ? handleDeselectAll : handleSelectAll}
            >
              {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
            </button>
            {selectedValues.length > 0 && (
              <button
                type="button"
                className="clear-all-button"
                onClick={handleClearAll}
                title="Clear all selections"
              >
                <X size={14} />
              </button>
            )}
          </div>
          
          <div className="multi-select-options">
            {options.map((option) => (
              <label key={option} className="multi-select-option">
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={() => handleSelect(option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectPerformance = () => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedProject, setSelectedProject] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [goLiveDateFilter, setGoLiveDateFilter] = useState([]);
  const [projectTasks, setProjectTasks] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const { dashboardData, rocketlaneAPI } = useDashboard();



  const analyzeTasks = (tasks) => {
    if (!tasks || tasks.length === 0) {
      return {
        totalTasks: 0,
        totalMilestones: 0,
        tasksCompleted: 0,
        milestonesCompleted: 0,
        overdueTasks: 0,
        overdueMilestones: 0,
        riskScore: 0
      };
    }

    const today = new Date();
    const analysis = {
      totalTasks: 0,
      totalMilestones: 0,
      tasksCompleted: 0,
      milestonesCompleted: 0,
      overdueTasks: 0,
      overdueMilestones: 0,
      riskScore: 0
    };

    tasks.forEach(task => {
      const isMilestone = task.type === 'MILESTONE';
      const isTask = task.type === 'TASK';
      
      if (isMilestone) {
        analysis.totalMilestones++;
        const status = task.status?.label || 'Unknown';
        
        if (status.toLowerCase().includes('completed')) {
          analysis.milestonesCompleted++;
        }

        // Check for overdue milestones
        if (task.dueDate && new Date(task.dueDate) < today && !status.toLowerCase().includes('completed')) {
          analysis.overdueMilestones++;
        }
      } else if (isTask) {
        analysis.totalTasks++;
        const status = task.status?.label || 'Unknown';
        
        if (status.toLowerCase().includes('completed')) {
          analysis.tasksCompleted++;
        }

        // Check for overdue tasks
        if (task.dueDate && new Date(task.dueDate) < today && !status.toLowerCase().includes('completed')) {
          analysis.overdueTasks++;
        }
      }
    });

    // Calculate risk score (simplified version)
    let riskScore = 0;
    if (analysis.overdueTasks > 0) {
      riskScore += Math.min(analysis.overdueTasks * 5, 20);
    }
    if (analysis.overdueMilestones > 0) {
      riskScore += Math.min(analysis.overdueMilestones * 10, 20);
    }
    analysis.riskScore = Math.min(riskScore, 100);

    return analysis;
  };

  const getRiskLevel = (count, thresholds) => {
    if (count >= thresholds.high) return 'high';
    if (count >= thresholds.medium) return 'medium';
    return 'low';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'high': return '#ef4444'; // Red
      case 'medium': return '#f59e0b'; // Orange
      case 'low': return '#10b981'; // Green
      default: return '#64748b'; // Gray
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'high': return <AlertTriangle size={14} />;
      case 'medium': return <AlertCircle size={14} />;
      case 'low': return <CheckCircle size={14} />;
      default: return <AlertCircle size={14} />;
    }
  };

  if (!dashboardData) {
    return <div className="loading">Loading project performance data...</div>;
  }

  const { projects } = dashboardData.details;

  // Handle case where projects might be undefined or empty
  if (!projects || !Array.isArray(projects) || projects.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Project Performance</h2>
        </div>
        <div className="empty-state">
          <BarChart3 size={48} color="#9ca3af" />
          <p>No project data available</p>
          <p className="text-sm text-gray-500">Project performance data will appear here when available</p>
        </div>
      </div>
    );
  }

  // Helper function to get go-live date from project fields
  const getGoLiveDate = (project) => {
    const goLiveDateField = project.fields?.find(field => field.fieldId === 1601377);
    return goLiveDateField?.fieldValue || null;
  };

  // Transform project data to match our table structure
  const transformedProjects = projects.map(project => ({
    id: project.projectId,
    projectName: project.projectName,
    progressPercentage: project.progressPercentage || project.progress || 0,
    status: project.status?.label || project.status?.value || project.status || 'unknown',
    owner: project.owner ? `${project.owner.firstName} ${project.owner.lastName}`.trim() : 'Unknown',
    startDate: project.startDate,
    endDate: 0,
    goLiveDate: getGoLiveDate(project),
    // Keep the original project data for the modal
    originalProject: project
  }));

  // Apply status, owner, and go-live date filtering
  const filteredProjects = useMemo(() => {
    return transformedProjects.filter(project => {
      const statusMatch = statusFilter.length === 0 || statusFilter.includes(project.status);
      const ownerMatch = ownerFilter.length === 0 || ownerFilter.includes(project.owner);
      const goLiveDateMatch = goLiveDateFilter.length === 0 || goLiveDateFilter.includes(project.goLiveDate);
      return statusMatch && ownerMatch && goLiveDateMatch;
    });
  }, [transformedProjects, statusFilter, ownerFilter, goLiveDateFilter]);

  const fetchTasksForProjects = async (page = 1, append = false) => {
    if (page === 1) {
      setLoadingTasks(true);
      setCurrentPage(1);
    } else {
      setLoadingMore(true);
    }
    
    const tasksData = {};
    const projectsPerPage = 10;
    const startIndex = (page - 1) * projectsPerPage;
    const endIndex = startIndex + projectsPerPage;
    const projectsToFetch = filteredProjects.slice(startIndex, endIndex);
    
    try {
      for (const project of projectsToFetch) {
        if (!projectTasks[project.id]) {
          console.log(`Fetching tasks for project: ${project.id} (${project.projectName})`);
          const projectTasksData = await rocketlaneAPI.getTasks(project.id);
          tasksData[project.id] = projectTasksData;
        }
      }
      
      if (append) {
        setProjectTasks(prev => ({ ...prev, ...tasksData }));
      } else {
        setProjectTasks(tasksData);
      }
      
      // Check if there are more projects to load
      setHasMoreProjects(endIndex < filteredProjects.length);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoadingTasks(false);
      setLoadingMore(false);
    }
  };

  // Fetch tasks for filtered projects when filters change
  useEffect(() => {
    if (ownerFilter.length > 0 && goLiveDateFilter.length > 0 && filteredProjects.length > 0) {
      fetchTasksForProjects(1, false);
    }
  }, [ownerFilter, goLiveDateFilter]);

  // Get unique statuses, owners, and go-live dates for filter dropdowns
  const uniqueStatuses = Array.from(new Set(transformedProjects.map(p => p.status))).sort();
  const uniqueOwners = Array.from(new Set(transformedProjects.map(p => p.owner))).sort();
  const uniqueGoLiveDates = Array.from(new Set(transformedProjects.map(p => p.goLiveDate).filter(date => date !== null))).sort();

  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Handle sort click
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={14} />;
    }
    return sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
  };

  // Get projects that have been loaded with tasks
  const loadedProjects = filteredProjects.filter(project => projectTasks.hasOwnProperty(project.id));
  
  // Apply sorting if configured
  let sortedProjects = loadedProjects; // Use loadedProjects here
  if (sortConfig.key) {
    sortedProjects = sortData(loadedProjects, sortConfig.key, sortConfig.direction);
  }

  // Calculate summary metrics
  const getSummaryMetrics = () => {
    const totalProjects = sortedProjects.length;
    const averageProgress = totalProjects > 0 
      ? sortedProjects.reduce((sum, project) => sum + project.progressPercentage, 0) / totalProjects 
      : 0;
    
    // Calculate risk metrics from all projects
    let totalTasks = 0;
    let totalCompletedTasks = 0;
    let totalCompletedMilestones = 0;
    let totalOverdueTasks = 0;
    let totalOverdueMilestones = 0;
    let totalRiskScore = 0;
    let projectsWithTasks = 0;

    sortedProjects.forEach(project => {
      const tasks = projectTasks[project.id] || [];
      const analysis = analyzeTasks(tasks);
      if (analysis.totalTasks + analysis.totalMilestones > 0) {
        totalTasks += analysis.totalTasks;
        totalCompletedTasks += analysis.tasksCompleted;
        totalCompletedMilestones += analysis.milestonesCompleted;
        totalOverdueTasks += analysis.overdueTasks;
        totalOverdueMilestones += analysis.overdueMilestones;
        totalRiskScore += analysis.riskScore;
        projectsWithTasks++;
      }
    });

    const averageRiskScore = projectsWithTasks > 0 ? totalRiskScore / projectsWithTasks : 0;
    
    return {
      totalProjects,
      averageProgress,
      totalTasks,
      totalCompletedTasks,
      totalCompletedMilestones,
      totalOverdueTasks,
      totalOverdueMilestones,
      averageRiskScore
    };
  };

  const summaryMetrics = getSummaryMetrics();

  // Handle project click
  const handleProjectClick = (project) => {
    setSelectedProject(project.originalProject);
    setIsProjectModalOpen(true);
  };

  // Handle load more projects
  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchTasksForProjects(nextPage, true);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Project Performance</h2>
        <div className="card-actions">
          <button className="btn-secondary">Export Data</button>
        </div>
      </div>

      <div className="utilization-summary">
        <div className="summary-item">
          <span className="summary-label">Total Projects</span>
          <span className="summary-value">{summaryMetrics.totalProjects}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Average Progress</span>
          <span className="summary-value">{summaryMetrics.averageProgress.toFixed(1)}%</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Tasks</span>
          <span className="summary-value">{summaryMetrics.totalTasks}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Completed Tasks</span>
          <span className="summary-value">{summaryMetrics.totalCompletedTasks}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">At Risk Tasks</span>
          <span className="summary-value">{summaryMetrics.totalOverdueTasks}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Risk Score</span>
          <span className="summary-value">{summaryMetrics.averageRiskScore.toFixed(0)}/100</span>
        </div>
      </div>

      <div className="utilization-table">
        <h4>Project Performance Details</h4>
        
        {/* Filters */}
        <div className="filter-section">
          <div className="filter-row">
            <div className="filter-group">
              <label className="filter-label">Filter by Status:</label>
              <MultiSelect
                options={uniqueStatuses}
                selectedValues={statusFilter}
                onChange={setStatusFilter}
                placeholder="All Statuses"
              />
            </div>
            
            <div className="filter-group">
              <label className="filter-label">Filter by Owner:</label>
              <MultiSelect
                options={uniqueOwners}
                selectedValues={ownerFilter}
                onChange={setOwnerFilter}
                placeholder="All Owners"
              />
            </div>

            <div className="filter-group">
              <label className="filter-label">Filter by Go-Live Date:</label>
              <MultiSelect
                options={uniqueGoLiveDates}
                selectedValues={goLiveDateFilter}
                onChange={setGoLiveDateFilter}
                placeholder="All Go-Live Dates"
              />
            </div>
          </div>
          
          {/* Requirement Messages */}
          {(ownerFilter.length === 0 || goLiveDateFilter.length === 0) && (
            <div className="owner-requirement">
              <p>⚠️ Please select at least one owner and one go-live date to view project details.</p>
            </div>
          )}
          
          {/* Filter Summary */}
          {(statusFilter.length > 0 || ownerFilter.length > 0 || goLiveDateFilter.length > 0) && (
            <div className="filter-summary">
              <span className="summary-label">Active filters:</span>
              <div className="filter-tags">
                {statusFilter.length > 0 && (
                  <span className="filter-tag">
                    Status: {statusFilter.join(', ')}
                    <button 
                      className="filter-tag-remove" 
                      onClick={() => setStatusFilter([])}
                    >
                      ×
                    </button>
                  </span>
                )}
                {ownerFilter.length > 0 && (
                  <span className="filter-tag">
                    Owner: {ownerFilter.join(', ')}
                    <button 
                      className="filter-tag-remove" 
                      onClick={() => setOwnerFilter([])}
                    >
                      ×
                    </button>
                  </span>
                )}
                {goLiveDateFilter.length > 0 && (
                  <span className="filter-tag">
                    Go-Live Date: {goLiveDateFilter.join(', ')}
                    <button 
                      className="filter-tag-remove" 
                      onClick={() => setGoLiveDateFilter([])}
                    >
                      ×
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Only show table if owner and go-live date are selected */}
        {ownerFilter.length > 0 && goLiveDateFilter.length > 0 ? (
          <div className="table-container">
          <table>
                        <thead>
              <tr>
                <th onClick={() => handleSort('projectName')} className="sortable-header">
                  Project Name
                  {getSortIcon('projectName')}
                </th>
                <th onClick={() => handleSort('progressPercentage')} className="sortable-header">
                  <Target size={14} />
                  Progress
                  {getSortIcon('progressPercentage')}
                </th>
                <th onClick={() => handleSort('totalTasks')} className="sortable-header">
                  <CheckCircle size={14} />
                  Tasks (Total/Completed)
                  {getSortIcon('totalTasks')}
                </th>
                <th onClick={() => handleSort('overdueTasks')} className="sortable-header">
                  <AlertTriangle size={14} />
                  At Risk Tasks
                  {getSortIcon('overdueTasks')}
                </th>
                <th onClick={() => handleSort('overdueMilestones')} className="sortable-header">
                  <AlertCircle size={14} />
                  At Risk Milestones
                  {getSortIcon('overdueMilestones')}
                </th>
                <th onClick={() => handleSort('riskScore')} className="sortable-header">
                  <BarChart3 size={14} />
                  Risk Score
                  {getSortIcon('riskScore')}
                </th>
                <th onClick={() => handleSort('status')} className="sortable-header">
                  Status
                  {getSortIcon('status')}
                </th>
              </tr>
            </thead>
                        <tbody>
              {loadingTasks ? (
                <tr>
                  <td colSpan="7" className="loading-tasks-row">
                    <div className="loading-tasks">
                      <div className="loading-spinner"></div>
                      <span>Loading project tasks and risk analysis...</span>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedProjects.map((project) => {
                  const tasks = projectTasks[project.id] || [];
                  const analysis = analyzeTasks(tasks);
                  
                  return (
                    <tr key={project.id} className="clickable-row" onClick={() => handleProjectClick(project)}>
                      <td>
                        <div className="project-name-cell">
                          <strong>{project.projectName}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="progress-cell">
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${project.progressPercentage}%` }}
                            ></div>
                          </div>
                          <span className="progress-text">{project.progressPercentage}%</span>
                        </div>
                      </td>
                      <td>
                        <div className="tasks-cell">
                          <span className="total-tasks">{analysis.totalTasks}</span>
                          <span className="completed-tasks">({analysis.tasksCompleted} completed)</span>
                        </div>
                      </td>
                      <td>
                        <div className="risk-cell">
                          {analysis.overdueTasks > 0 ? (
                            <span className="risk-badge high" style={{ color: getRiskColor('high') }}>
                              {getRiskIcon('high')} {analysis.overdueTasks}
                            </span>
                          ) : (
                            <span className="risk-badge low" style={{ color: getRiskColor('low') }}>
                              {getRiskIcon('low')} 0
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="risk-cell">
                          {analysis.overdueMilestones > 0 ? (
                            <span className="risk-badge high" style={{ color: getRiskColor('high') }}>
                              {getRiskIcon('high')} {analysis.overdueMilestones}
                            </span>
                          ) : (
                            <span className="risk-badge low" style={{ color: getRiskColor('low') }}>
                              {getRiskIcon('low')} 0
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="risk-score-cell">
                          <span 
                            className="risk-score" 
                            style={{ color: getRiskColor(analysis.riskScore >= 70 ? 'high' : analysis.riskScore >= 40 ? 'medium' : 'low') }}
                          >
                            {analysis.riskScore.toFixed(0)}/100
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${project.status.toLowerCase().replace(/\s+/g, '-')}`}>
                          {project.status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
                     </table>
           
           {/* Load More Button */}
           {hasMoreProjects && !loadingMore && (
             <div className="load-more-container">
               <button 
                 className="load-more-button" 
                 onClick={handleLoadMore}
               >
                 Load Next 10 Projects
               </button>
             </div>
           )}
           
           {loadingMore && (
             <div className="loading-more">
               <div className="loading-spinner"></div>
               <span>Loading more projects...</span>
             </div>
           )}
         </div>
                 ) : (
           <div className="no-owner-selected">
             <p>Please select at least one owner and one go-live date to view project details.</p>
           </div>
         )}
      </div>

      <ProjectDetailModal 
        project={selectedProject} 
        isOpen={isProjectModalOpen} 
        onClose={() => setIsProjectModalOpen(false)}
      />
    </div>
  );
 };

export default ProjectPerformance;

